import React, { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import {
    getAccountingProviders,
    getAccountingDocumentTypes,
    getAccountingSchema,
    getAccountingFieldMappings,
    createAccountingFieldMapping,
    updateAccountingFieldMapping,
    deleteAccountingFieldMapping,
    testAccountingMapping
} from '../../api/common';

const VALUE_SOURCES = ['internal', 'default', 'fixed', 'calculated'];
const TRANSFORMS = ['none', 'number', 'string', 'boolean', 'date_iso', 'date_short', 'uppercase', 'lowercase', 'lookup_table'];
const DIRECTIONS = [
    { value: 'outbound', label: 'outbound (Tinvaz → proveedor)' },
    { value: 'inbound',  label: 'inbound (proveedor → Tinvaz)' }
];

function _attr(v) {
    if (v === null || v === undefined) return '';
    return String(v).replace(/"/g, '&quot;');
}

// ── Field Tree rendering ──────────────────────────────────────────────────────

function FieldNode({ nodeKey, node, mappedPaths, onLeafClick, depth = 0 }) {
    const [expanded, setExpanded] = useState(false);
    const isLeaf = !!node.path;
    const isMapped = isLeaf && mappedPaths.has(node.path);
    const indent = depth * 16;

    if (isLeaf) {
        return (
            <div
                className="d-flex align-items-center gap-1 py-1 px-2 rounded cursor-pointer"
                style={{ paddingLeft: indent + 8, cursor: 'pointer', fontSize: 13 }}
                onClick={() => onLeafClick(node)}
            >
                {isMapped ? <span className="text-success me-1">✅</span> : <span className="me-1" style={{ width: 18 }} />}
                <code style={{ fontSize: 12 }}>{nodeKey}</code>
                <span className="text-muted ms-1">— {node.label}</span>
            </div>
        );
    }

    const label = node._isArray ? `${nodeKey}[]` : nodeKey;
    const children = node.children || {};

    return (
        <div>
            <div
                className="d-flex align-items-center gap-1 py-1 px-2 rounded"
                style={{ paddingLeft: indent + 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                onClick={() => setExpanded(e => !e)}
            >
                <span style={{ fontSize: 10 }}>{expanded ? '▼' : '▶'}</span>
                <span>{label}</span>
                {node._isArray && <span className="badge bg-secondary ms-1" style={{ fontSize: 10 }}>array</span>}
            </div>
            {expanded && Object.entries(children).map(([k, v]) => (
                <FieldNode key={k} nodeKey={k} node={v} mappedPaths={mappedPaths} onLeafClick={onLeafClick} depth={depth + 1} />
            ))}
        </div>
    );
}

// ── Modal helpers ─────────────────────────────────────────────────────────────

function buildMappingModalHtml(internalPath = '', mapping = null, defaultDirection = 'outbound') {
    const editing = mapping !== null;
    const vs = editing ? mapping.value_source : 'internal';
    const showDefault = ['default', 'fixed'].includes(vs);
    const dir = editing ? (mapping.direction || 'outbound') : defaultDirection;
    const tx = editing ? (mapping.transform || 'none') : 'none';
    const showLookup = tx === 'lookup_table';

    // En inbound: external_path es el origen (proveedor) y internal_path el destino (Tinvaz).
    // En outbound: al reves. Ajustamos las etiquetas dinamicamente para no confundir.
    const lblInterno = dir === 'inbound' ? 'Campo destino en Tinvaz (internal_path)' : 'Campo interno en Tinvaz (internal_path)';
    const lblExterno = dir === 'inbound' ? 'Campo origen en proveedor (external_path)' : 'Campo destino en proveedor (external_path)';

    return `
        <div class="text-start">
            <div class="mb-2">
                <label class="form-label fw-semibold">Direccion</label>
                <select id="swal-fm-direction" class="swal2-input">
                    ${DIRECTIONS.map(d => `<option value="${d.value}" ${dir === d.value ? 'selected' : ''}>${d.label}</option>`).join('')}
                </select>
                <small class="text-muted d-block">
                    outbound: enviar documentos Tinvaz al proveedor (POST). inbound: leer datos del proveedor (GET cuentas, terceros).
                </small>
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold" id="swal-fm-internal-label">${lblInterno}</label>
                <input id="swal-fm-internal" class="swal2-input" readonly value="${_attr(editing ? mapping.internal_path : internalPath)}" />
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold" id="swal-fm-external-label">${lblExterno}</label>
                <input id="swal-fm-external" class="swal2-input" placeholder="ej: client.identification"
                    value="${_attr(editing ? mapping.external_path : '')}" required />
            </div>
            <div class="mb-2 form-check ms-2" id="swal-fm-required-wrap" style="display:${dir === 'outbound' ? '' : 'none'}">
                <input type="checkbox" id="swal-fm-required" class="form-check-input"
                    ${editing && mapping.required_external ? 'checked' : ''} />
                <label class="form-check-label" for="swal-fm-required">Campo requerido en destino (solo outbound)</label>
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Origen del valor</label>
                <select id="swal-fm-source" class="swal2-input">
                    ${VALUE_SOURCES.map(s => `<option value="${s}" ${vs === s ? 'selected' : ''}>${s}</option>`).join('')}
                </select>
            </div>
            <div id="swal-fm-default-container" ${showDefault ? '' : 'style="display:none"'}>
                <div class="mb-2">
                    <label class="form-label fw-semibold">Valor por defecto</label>
                    <input id="swal-fm-default" class="swal2-input" placeholder="valor por defecto"
                        value="${_attr(editing && mapping.default_value !== null ? mapping.default_value : '')}" />
                </div>
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Transform</label>
                <select id="swal-fm-transform" class="swal2-input">
                    ${TRANSFORMS.map(t => `<option value="${t}" ${tx === t ? 'selected' : ''}>${t}</option>`).join('')}
                </select>
            </div>
            <div class="mb-2" id="swal-fm-lookup-wrap" style="display:${showLookup ? '' : 'none'}">
                <label class="form-label fw-semibold">lookup_map (JSON)</label>
                <textarea id="swal-fm-lookup" class="swal2-textarea" placeholder='{"debit":"Debito","credit":"Credito"}'>${editing && mapping.lookup_map ? mapping.lookup_map : ''}</textarea>
                <small class="text-muted">Diccionario clave-valor: cuando el valor del proveedor coincida con una clave, se reemplaza por su valor.</small>
            </div>
            <div class="mb-2">
                <label class="form-label fw-semibold">Notas (opcional)</label>
                <textarea id="swal-fm-notes" class="swal2-textarea">${editing ? (mapping.notes || '') : ''}</textarea>
            </div>
        </div>`;
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function FieldMappingsTab() {
    const [providers, setProviders] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [selectedDocType, setSelectedDocType] = useState('');
    const [direction, setDirection] = useState('outbound');
    const [schema, setSchema] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSchema, setLoadingSchema] = useState(false);
    const [showTestPanel, setShowTestPanel] = useState(false);
    const [testInput, setTestInput] = useState('');
    const [testResult, setTestResult] = useState(null);
    const abortRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const [provRes, dtRes] = await Promise.all([
                    getAccountingProviders(),
                    getAccountingDocumentTypes()
                ]);
                if (provRes?.data?.success) setProviders(provRes.data.data);
                if (dtRes?.data?.success) setDocumentTypes(dtRes.data.data);
            } catch { /* ignore */ }
        })();
    }, []);

    const loadMappings = useCallback(async (pid, dtype) => {
        if (!pid || !dtype) return;
        setLoading(true);
        try {
            const res = await getAccountingFieldMappings(pid, dtype);
            if (res?.data?.success) setMappings(res.data.data);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);

    const loadSchema = useCallback(async (dtype) => {
        if (!dtype) { setSchema(null); return; }
        setLoadingSchema(true);
        try {
            const res = await getAccountingSchema(dtype);
            if (res?.data?.success) setSchema(res.data.data);
        } catch { /* ignore */ }
        finally { setLoadingSchema(false); }
    }, []);

    function handleProviderChange(e) {
        const val = e.target.value;
        setSelectedProvider(val);
        setMappings([]);
        setTestResult(null);
        if (val && selectedDocType) loadMappings(val, selectedDocType);
    }

    function handleDocTypeChange(e) {
        const val = e.target.value;
        setSelectedDocType(val);
        setSchema(null);
        setMappings([]);
        setTestResult(null);
        loadSchema(val);
        if (selectedProvider && val) loadMappings(selectedProvider, val);
    }

    // Mappings filtrados por la direccion seleccionada arriba.
    // El backend devuelve todos: filtramos en cliente porque el set es pequeño.
    const mappingsFiltrados = mappings.filter(m => (m.direction || 'outbound') === direction);
    const mappedPaths = new Set(mappingsFiltrados.map(m => m.internal_path));

    async function openMappingModal(internalPath = '', mapping = null) {
        const editing = mapping !== null;
        const { value: payload } = await Swal.fire({
            title: editing ? 'Editar Mapeo' : 'Nuevo Mapeo',
            html: buildMappingModalHtml(internalPath, mapping, direction),
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            width: 560,
            didOpen: () => {
                // 1) Toggle "Valor por defecto" segun value_source
                const sourceSelect = document.getElementById('swal-fm-source');
                if (sourceSelect) {
                    sourceSelect.addEventListener('change', () => {
                        const container = document.getElementById('swal-fm-default-container');
                        if (container) {
                            container.style.display = ['default', 'fixed'].includes(sourceSelect.value) ? '' : 'none';
                        }
                    });
                }
                // 2) Toggle lookup_map segun transform
                const txSelect = document.getElementById('swal-fm-transform');
                if (txSelect) {
                    txSelect.addEventListener('change', () => {
                        const wrap = document.getElementById('swal-fm-lookup-wrap');
                        if (wrap) wrap.style.display = txSelect.value === 'lookup_table' ? '' : 'none';
                    });
                }
                // 3) Actualizar etiquetas + ocultar "requerido" cuando direction=inbound
                const dirSelect = document.getElementById('swal-fm-direction');
                if (dirSelect) {
                    dirSelect.addEventListener('change', () => {
                        const isInbound = dirSelect.value === 'inbound';
                        const lblInt = document.getElementById('swal-fm-internal-label');
                        const lblExt = document.getElementById('swal-fm-external-label');
                        const reqWrap = document.getElementById('swal-fm-required-wrap');
                        if (lblInt) lblInt.textContent = isInbound
                            ? 'Campo destino en Tinvaz (internal_path)'
                            : 'Campo interno en Tinvaz (internal_path)';
                        if (lblExt) lblExt.textContent = isInbound
                            ? 'Campo origen en proveedor (external_path)'
                            : 'Campo destino en proveedor (external_path)';
                        if (reqWrap) reqWrap.style.display = isInbound ? 'none' : '';
                    });
                }
            },
            preConfirm: () => {
                const direction       = document.getElementById('swal-fm-direction')?.value || 'outbound';
                const external_path   = document.getElementById('swal-fm-external')?.value?.trim();
                const required_external = !!document.getElementById('swal-fm-required')?.checked;
                const value_source    = document.getElementById('swal-fm-source')?.value;
                const default_value   = document.getElementById('swal-fm-default')?.value?.trim() || null;
                const transform       = document.getElementById('swal-fm-transform')?.value || 'none';
                const lookup_map_raw  = document.getElementById('swal-fm-lookup')?.value?.trim() || '';
                const notes           = document.getElementById('swal-fm-notes')?.value?.trim() || '';

                if (!external_path) { Swal.showValidationMessage('El campo externo es requerido'); return false; }
                if (['default', 'fixed'].includes(value_source) && !default_value) {
                    Swal.showValidationMessage(`Valor por defecto es requerido cuando el origen es '${value_source}'`);
                    return false;
                }
                let lookup_map = null;
                if (transform === 'lookup_table') {
                    if (!lookup_map_raw) {
                        Swal.showValidationMessage('lookup_map es requerido cuando transform=lookup_table');
                        return false;
                    }
                    try {
                        JSON.parse(lookup_map_raw);
                        lookup_map = lookup_map_raw;
                    } catch {
                        Swal.showValidationMessage('lookup_map no es JSON valido');
                        return false;
                    }
                }
                return {
                    direction, external_path,
                    required_external: direction === 'outbound' ? required_external : false,
                    value_source, default_value, transform, lookup_map, notes
                };
            }
        });

        if (!payload) return;

        const internal_path = editing ? mapping.internal_path : internalPath;
        const body = {
            ...payload,
            internal_path,
            provider_id: selectedProvider,
            document_type: selectedDocType
        };

        try {
            let res;
            if (editing) {
                res = await updateAccountingFieldMapping(mapping._id, payload);
            } else {
                res = await createAccountingFieldMapping(body);
            }
            if (res?.data?.success) {
                Swal.fire({ icon: 'success', title: editing ? 'Mapeo actualizado' : 'Mapeo creado', timer: 1500, showConfirmButton: false });
                loadMappings(selectedProvider, selectedDocType);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: res?.data?.error || 'Error al guardar' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Error de conexión con el servidor' });
        }
    }

    async function handleLeafClick(node) {
        // Buscar mapping de la direccion actualmente seleccionada
        const existing = mappingsFiltrados.find(m => m.internal_path === node.path);
        if (existing) {
            openMappingModal(node.path, existing);
        } else {
            openMappingModal(node.path, null);
        }
    }

    async function handleDeleteMapping(mapping) {
        const { isConfirmed } = await Swal.fire({
            title: `¿Eliminar mapeo '${mapping.external_path}'?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33'
        });
        if (!isConfirmed) return;
        try {
            const res = await deleteAccountingFieldMapping(mapping._id);
            if (res?.data?.success) {
                Swal.fire({ icon: 'success', title: 'Mapeo eliminado', timer: 1200, showConfirmButton: false });
                loadMappings(selectedProvider, selectedDocType);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: res?.data?.error || 'No se pudo eliminar' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Error de conexión con el servidor' });
        }
    }

    async function handleTestMapping() {
        let parsed;
        try {
            parsed = JSON.parse(testInput);
        } catch {
            Swal.fire({ icon: 'error', title: 'JSON inválido', text: 'El texto ingresado no es JSON válido' });
            return;
        }
        try {
            const res = await testAccountingMapping({
                provider_id: selectedProvider,
                document_type: selectedDocType,
                sample_data: parsed
            });
            if (res?.data?.success) {
                setTestResult(res.data.data);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: res?.data?.error || 'Error al ejecutar la prueba' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Error de conexión con el servidor' });
        }
    }

    const canShowContent = selectedProvider && selectedDocType;

    return (
        <div>
            {/* Filters */}
            <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
                <select className="form-select form-select-sm" style={{ maxWidth: 220 }} value={selectedProvider} onChange={handleProviderChange}>
                    <option value="">-- Proveedor --</option>
                    {providers.map(p => <option key={p._id} value={p._id}>{p.display_name}</option>)}
                </select>
                <select className="form-select form-select-sm" style={{ maxWidth: 220 }} value={selectedDocType} onChange={handleDocTypeChange}>
                    <option value="">-- Tipo de Documento --</option>
                    {documentTypes.map(dt => <option key={dt.code} value={dt.code}>{dt.label}</option>)}
                </select>
                <div className="btn-group btn-group-sm" role="group" aria-label="Direccion del mapeo">
                    {DIRECTIONS.map(d => (
                        <button
                            key={d.value}
                            type="button"
                            className={`btn ${direction === d.value ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => { setDirection(d.value); setTestResult(null); }}
                            title={d.label}
                        >
                            {d.value}
                        </button>
                    ))}
                </div>
                <small className="text-muted ms-2">
                    {direction === 'outbound'
                        ? 'Tinvaz → proveedor (POST de comprobantes)'
                        : 'proveedor → Tinvaz (GET cuentas, terceros)'}
                </small>
            </div>

            {!canShowContent && (
                <div className="text-muted text-center py-5">
                    Selecciona un proveedor y un tipo de documento para comenzar
                </div>
            )}

            {canShowContent && (
                <div className="row g-3">
                    {/* Left panel — field tree */}
                    <div className="col-md-4">
                        <div className="card w-100">
                            <div className="card-header py-2 fw-semibold w-100" style={{ fontSize: 13 }}>
                                Campos del modelo
                            </div>
                            <div className="card-body p-1 w-100" style={{ overflowY: 'auto', maxHeight: 600 }}>
                                {loadingSchema && <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>}
                                {!loadingSchema && schema && Object.entries(schema.fields).map(([k, v]) => (
                                    <FieldNode key={k} nodeKey={k} node={v} mappedPaths={mappedPaths} onLeafClick={handleLeafClick} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right panel — mappings table */}
                    <div className="col-md-8">
                        {loading ? (
                            <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Dir</th>
                                            <th>Campo interno (Tinvaz)</th>
                                            <th>Campo externo (proveedor)</th>
                                            <th>Origen</th>
                                            <th>Req</th>
                                            <th>Transform</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mappingsFiltrados.length === 0 && (
                                            <tr><td colSpan={7} className="text-center text-muted py-3">
                                                Sin mapeos {direction}. Haz clic en un campo del árbol para crear uno.
                                            </td></tr>
                                        )}
                                        {mappingsFiltrados.map(m => {
                                            const dir = m.direction || 'outbound';
                                            return (
                                                <tr key={m._id}>
                                                    <td>
                                                        <span className={`badge ${dir === 'inbound' ? 'bg-info' : 'bg-primary'}`} style={{ fontSize: 10 }}>
                                                            {dir === 'inbound' ? '← in' : '→ out'}
                                                        </span>
                                                    </td>
                                                    <td><code style={{ fontSize: 11 }}>{m.internal_path}</code></td>
                                                    <td>
                                                        <code style={{ fontSize: 11 }}>{m.external_path}</code>
                                                        {m.transform === 'lookup_table' && (
                                                            <span className="badge bg-warning bg-opacity-25 text-warning border border-warning ms-1" style={{ fontSize: 10 }} title={m.lookup_map}>
                                                                lookup
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td><span className="badge bg-secondary">{m.value_source}</span></td>
                                                    <td>{dir === 'outbound' && m.required_external ? '✅' : '—'}</td>
                                                    <td>{m.transform !== 'none' ? m.transform : '—'}</td>
                                                    <td>
                                                        <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openMappingModal(m.internal_path, m)}>✏️</button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteMapping(m)}>🗑️</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Test Panel — solo aplica para outbound (motor existente).
                            Inbound se prueba ejecutando el endpoint de lectura real. */}
                        <div className="mt-3">
                            <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => { setShowTestPanel(v => !v); setTestResult(null); }}
                                disabled={direction === 'inbound'}
                                title={direction === 'inbound' ? 'El panel de prueba solo aplica a outbound. Para inbound, ejecuta el endpoint de lectura real.' : ''}
                            >
                                {showTestPanel ? '▼' : '▶'} Probar Motor de Mapeo (outbound)
                            </button>
                            {showTestPanel && (
                                <div className="border rounded p-3 mt-2">
                                    <div className="mb-2">
                                        <textarea
                                            className="form-control form-control-sm font-monospace"
                                            rows={5}
                                            placeholder='{ "cliente": { "nit": "900123456" }, "total": 100000 }'
                                            value={testInput}
                                            onChange={e => setTestInput(e.target.value)}
                                        />
                                    </div>
                                    <button className="btn btn-sm btn-primary" onClick={handleTestMapping}>Ejecutar Prueba</button>
                                    {testResult && (
                                        <div className="mt-3">
                                            <div className="mb-1 fw-semibold" style={{ fontSize: 13 }}>Resultado:</div>
                                            <pre className="bg-light p-2 rounded" style={{ fontSize: 12, maxHeight: 200, overflowY: 'auto' }}>
                                                {JSON.stringify(testResult.result, null, 2)}
                                            </pre>
                                            {testResult.warnings?.length > 0 && (
                                                <div className="mt-2">
                                                    <div className="fw-semibold text-warning mb-1" style={{ fontSize: 13 }}>Advertencias:</div>
                                                    {testResult.warnings.map((w, i) => (
                                                        <div key={i} className="text-warning" style={{ fontSize: 12 }}>⚠ {w}</div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                                Mapeos aplicados: {testResult.mappings_applied}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
